import { useState } from 'react';
import { useSEO } from '../../utils/seo';

function Home() {
  const [activeTab, setActiveTab] = useState('about');

  // SEO 설정
  useSEO({
    title: '부평구 우울증 불안장애 수면장애 연우정신건강의학과',
    description: '인천 부평구 굴포천역 근처 연우정신건강의학과. 우울증, 불안장애, 수면장애 전문 치료. 전연우 원장의 따뜻하고 전문적인 진료로 마음의 건강을 회복하세요. 부평구 정신건강의학과 전문의원으로 심리상담, TMS 비약물치료, 약물치료를 제공합니다.',
    keywords: '부평구 정신건강의학과, 굴포천역 정신과, 우울증 치료, 불안장애 치료, 수면장애 치료',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://yeonwoo-clinic.com'}/`,
    ogTitle: '부평구 우울증 불안장애 수면장애 연우정신건강의학과',
    ogDescription: '인천 부평구 굴포천역 근처 연우정신건강의학과. 우울증, 불안장애, 수면장애 전문 치료. 전연우 원장의 따뜻하고 전문적인 진료로 마음의 건강을 회복하세요.',
    ogType: 'website',
    ogUrl: `${import.meta.env.VITE_SITE_URL || 'https://yeonwoo-clinic.com'}/`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'MedicalClinic',
      name: '연우정신건강의학과',
      description: '인천 부평구 굴포천역 근처 정신건강의학과 전문의원. 우울증, 불안장애, 수면장애 전문 치료.',
      url: import.meta.env.VITE_SITE_URL || 'https://yeonwoo-clinic.com',
      telephone: '032-330-1210',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '길주로641, 3층 302호',
        addressLocality: '부평구',
        addressRegion: '인천광역시',
        addressCountry: 'KR',
        postalCode: '21389',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 37.4897,
        longitude: 126.7222,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Friday'],
          opens: '10:00',
          closes: '19:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '10:00',
          closes: '16:00',
        },
      ],
      medicalSpecialty: ['Psychiatry', 'Mental Health'],
      priceRange: '$$',
    },
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  연우정신건강의학과
                </h1>
                <p className="text-sm text-gray-600">마음의 건강을 위한 전문 진료</p>
              </div>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#about" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                병원소개
              </a>
              <a href="#doctor" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                의료진
              </a>
              <a href="#services" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                진료안내
              </a>
              <a href="#contact" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
                오시는길
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                마음의 건강을<br />
                <span className="text-teal-600">함께 돌보겠습니다</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                연우정신건강의학과는 환자분들의 정신건강 회복을 위해<br />
                전문적이고 따뜻한 진료를 제공합니다.
              </p>
              <a href="tel:032-330-1210" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer">
                진료예약 전화하기
              </a>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/e69a33c002919111400d8c568f9bddcc/d8a9b96eb1dc91bf52bbcc4ab79217a9.png"
                alt="연우정신건강의학과 진료실"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">병원 소개</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              연우정신건강의학과는 환자분들의 마음의 건강을 최우선으로 생각하며,<br />
              개인별 맞춤 치료를 통해 건강한 일상으로의 복귀를 돕습니다.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-heart-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">개인별 맞춤 치료</h4>
              <p className="text-gray-600">
                환자분 개개인의 상황과 증상에 맞는<br />
                최적의 치료 계획을 수립합니다.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">안전한 진료 환경</h4>
              <p className="text-gray-600">
                프라이버시가 보장되는 안전하고<br />
                편안한 진료 환경을 제공합니다.
              </p>
            </div>

            <div className="text-center p-8 bg-gray-50 rounded-2xl">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">지속적인 관리</h4>
              <p className="text-gray-600">
                지속적인 관리와 체계적인 상담을 통해<br />
                정신건강을 유지합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section id="doctor" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">의료진 소개</h3>
            <p className="text-xl text-gray-600">
              풍부한 경험와 전문성을 바탕으로 최고의 진료를 제공합니다.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-teal-100 via-blue-50 to-indigo-100 rounded-3xl shadow-xl p-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="text-center md:text-left">
                  <img
                    src="https://static.readdy.ai/image/e69a33c002919111400d8c568f9bddcc/3ce2ba3e5a8918b5c5c0c600cae12ed0.png"
                    alt="전연우 원장님"
                    className="w-64 h-64 rounded-full mx-auto md:mx-0 object-cover shadow-xl mb-6 border-4 border-white"
                  />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">전연우 원장</h4>
                  <p className="text-lg text-teal-600 mb-6 font-semibold">정신건강의학과 전문의</p>

                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">학력</h5>
                      <ul className="text-gray-600 space-y-1">
                        <li>• 가톨릭관동대학교 의과대학 졸업</li>
                        <li>• 가톨릭중앙의료원 인턴</li>
                        <li>• 국립공주병원 레지던트</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">경력</h5>
                      <ul className="text-gray-600 space-y-1">
                        <li>• 전) 두드림 정신건강의학과의원 부원장</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">전문 분야</h5>
                      <ul className="text-gray-600 space-y-1">
                        <li>• 우울증, 불안장애 치료</li>
                        <li>• TMS 비약물치료</li>
                        <li>• 수면장애 치료</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">진료 안내</h3>
            <p className="text-xl text-gray-600">
              다양한 정신건강 문제에 대한 전문적인 진료를 제공합니다.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1. 우울증 치료 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-emotion-sad-line text-blue-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">우울증 치료</h4>
              <p className="text-gray-600 mb-4">
                지속적인 우울감, 무기력감 등의 증상을 전문적으로 진단하고 치료합니다.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 약물치료</li>
                <li>• 인지행동치료</li>
                <li>• 정신치료</li>
              </ul>
            </div>

            {/* 2. 불안장애 치료 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-red-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">불안장애 치료</h4>
              <p className="text-gray-600 mb-4">
                과도한 불안, 공황장애 등의 불안장애를 체계적으로 치료합니다.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 공황장애 치료</li>
                <li>• 사회불안장애</li>
                <li>• 범불안장애</li>
              </ul>
            </div>

            {/* 3. TMS 비약물치료 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-pulse-line text-green-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">TMS 비약물치료</h4>
              <p className="text-gray-600 mb-4">
                경두개 자기자극술(TMS)을 통한 안전하고 효과적인 비약물 치료를 제공합니다.
              </p>
            </div>

            {/* 4. 심리상담 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-3-line text-yellow-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">심리상담</h4>
              <p className="text-gray-600 mb-4">
                개인의 심리적 어려움에 대한 전문적인 상담을 제공합니다.
              </p>
            </div>

            {/* 5. ADHD 치료 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-focus-3-line text-indigo-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">ADHD 치료</h4>
              <p className="text-gray-600 mb-4">
                주의력결핍 과잉행동장애(ADHD)에 대한 전문적인 진단과 치료를 제공합니다.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 주의력 평가</li>
                <li>• 행동 치료</li>
                <li>• 약물 치료</li>
              </ul>
            </div>

            {/* 6. 수면장애 치료 */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-moon-line text-purple-600 text-xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">수면장애 치료</h4>
              <p className="text-gray-600 mb-4">
                불면증, 수면 무호흡증 등 다양한 수면 문제를 전문적으로 진단하고 치료합니다.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 수면 패턴 분석</li>
                <li>• 불면증 치료</li>
                <li>• 수면 위생 교육</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">진료 시간</h3>
            <p className="text-xl text-gray-600">
              환자분들의 편의를 위해 충분한 진료 시간을 확보하고 있습니다.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-teal-500">
                  <tr>
                    <th className="px-6 py-4 text-left text-lg font-semibold text-white">요일</th>
                    <th className="px-6 py-4 text-left text-lg font-semibold text-white">진료시간</th>
                    <th className="px-6 py-4 text-left text-lg font-semibold text-white">휴게시간</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-teal-50 transition-colors">
                    <td className="px-6 py-5 text-gray-900 font-medium">월, 화, 수, 금</td>
                    <td className="px-6 py-5 font-semibold text-teal-600">10:00 AM ~ 07:00 PM</td>
                    <td className="px-6 py-5 text-gray-600">01:00 PM ~ 02:00 PM</td>
                  </tr>
                  <tr className="hover:bg-teal-50 transition-colors">
                    <td className="px-6 py-5 text-gray-900 font-medium">토</td>
                    <td className="px-6 py-5 font-semibold text-teal-600">10:00 AM ~ 04:00 PM</td>
                    <td className="px-6 py-5 text-gray-600">01:00 PM ~ 01:30 PM</td>
                  </tr>
                </tbody>
              </table>
              
              <div className="p-6 bg-gradient-to-r from-teal-50 to-blue-50 border-t border-teal-100">
                <div className="flex items-center justify-center space-x-2 text-gray-700">
                  <i className="ri-calendar-close-line text-red-500 text-xl"></i>
                  <span className="font-medium">목요일/일요일/공휴일은 휴진입니다</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">오시는길</h3>
            <p className="text-xl text-gray-600">
              <strong className="text-teal-600 font-semibold">굴포천역 8번출구</strong> <strong className="text-blue-600 font-semibold">다빈치안경 건물 3층</strong>에 위치하고 있습니다.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Map */}
            <div>
              <div className="bg-gray-50 rounded-2xl p-8 h-96 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/e69a33c002919111400d8c568f9bddcc/14373cf0652309e5479591c2953faf5f.png"
                  alt="연우정신건강의학과 위치 지도"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            </div>

            {/* Information */}
            <div className="space-y-8">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-6">병원 정보</h4>
                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">주소</p>
                      <p className="text-gray-600">
                        인천광역시 부평구 길주로641, 3층 302호
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">전화번호</p>
                      <a href="tel:032-330-1210" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">032-330-1210</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-teal-50 rounded-2xl p-6 text-center">
                <h5 className="text-lg font-semibold text-gray-900 mb-2">
                  진료 예약 및 상담 문의
                </h5>
                <p className="text-gray-700 mb-4">
                  전화로 간편하게 예약하세요.
                </p>
                <a href="tel:032-330-1210" className="inline-block bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                  예약 바로 하기
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 mt-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-sm">
              연우정신건강의학과. All rights reserved.
            </p>
          </div>
        </footer>
      </section>
    </div>
  );
}

export default Home;
